import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeatureModule, FeatureRegistryState, RegistrationResult } from './types';
import { StoreWithReducerManager, isDynamicStore } from '../dynamicStore';
import { RootState } from '../types';
import {
  createFeaturePersistReducers,
  DEFAULT_PERSISTENCE_CONFIG,
  setPersistReducer,
} from './persistence';
import { persistReducer as reduxPersistReducer } from 'redux-persist';
import { addToWhitelist, removeFromWhitelist } from '../persistConfig';
import { updatePersistence } from './storeReference';
import { injectFeature, removeReducer } from '../dynamicPersistStore';
import { addToSyncBlacklist, addToSyncWhitelist, DEFAULT_SYNC_CONFIG } from './sync';

// Initialize the persist reducer function
setPersistReducer(reduxPersistReducer);

// Map to keep track of all feature modules
const featureModules: Map<string, FeatureModule> = new Map();

// Registry state slice
const initialState: FeatureRegistryState = {
  registeredFeatures: {},
};

const featureRegistrySlice = createSlice({
  name: 'featureRegistry',
  initialState,
  reducers: {
    registerFeature: (state, action: PayloadAction<string>) => {
      state.registeredFeatures[action.payload] = true;
    },
    unregisterFeature: (state, action: PayloadAction<string>) => {
      delete state.registeredFeatures[action.payload];
    },
  },
});

export const { registerFeature, unregisterFeature } = featureRegistrySlice.actions;
export const featureRegistryReducer = featureRegistrySlice.reducer;

/**
 * Register a feature module with the registry
 */
export function registerFeatureModule(
  featureModule: FeatureModule,
  store: StoreWithReducerManager,
): RegistrationResult {
  try {
    // Check if the store supports dynamic reducers
    if (!isDynamicStore(store)) {
      return {
        success: false,
        featureId: featureModule.id,
        error: 'Store does not support dynamic reducers',
      };
    }

    // Check if feature is already registered
    if (isFeatureRegistered(store.getState(), featureModule.id)) {
      return {
        success: true,
        featureId: featureModule.id,
      };
    }

    // Check dependencies
    if (featureModule.dependencies && featureModule.dependencies.length > 0) {
      for (const dependency of featureModule.dependencies) {
        if (!isFeatureRegistered(store.getState(), dependency)) {
          return {
            success: false,
            featureId: featureModule.id,
            error: `Missing dependency: ${dependency}`,
          };
        }
      }
    }

    // Handle sync configuration - apply defaults if not provided
    const syncConfig = featureModule.sync
      ? {
          ...DEFAULT_SYNC_CONFIG,
          ...featureModule.sync,
        }
      : DEFAULT_SYNC_CONFIG;

    if (syncConfig.enabled) {
      // Only process sync config if sync is enabled for this feature

      // Add feature sync blacklist items if provided
      if (syncConfig.blacklist && syncConfig.blacklist.length > 0) {
        addToSyncBlacklist(syncConfig.blacklist);
        console.log(
          `Feature ${featureModule.id} sync blacklist items added:`,
          syncConfig.blacklist,
        );
      }

      // Add feature sync whitelist items if provided
      if (syncConfig.whitelist && syncConfig.whitelist.length > 0) {
        addToSyncWhitelist(syncConfig.whitelist);
        console.log(
          `Feature ${featureModule.id} sync whitelist items added:`,
          syncConfig.whitelist,
        );
      }

      console.log(`Feature ${featureModule.id} sync enabled and configured`);
    } else {
      // If sync is disabled (default), ensure all actions from this feature are blacklisted
      const featurePrefix = `${featureModule.id}/`;
      addToSyncBlacklist([featurePrefix]);
      console.log(
        `Feature ${featureModule.id} sync disabled (default) - all actions blacklisted with prefix: ${featurePrefix}`,
      );
    }

    // Use the dynamic persist store manager for injection
    const injected = injectFeature(featureModule);

    if (!injected) {
      return {
        success: false,
        featureId: featureModule.id,
        error: 'Failed to inject feature into dynamic store',
      };
    }

    // Add to modules map
    featureModules.set(featureModule.id, featureModule);

    // Dispatch registration action
    store.dispatch(registerFeature(featureModule.id));

    // Check if persistence is actually enabled for better logging
    const persistenceEnabled =
      featureModule.persistence?.enabled ?? DEFAULT_PERSISTENCE_CONFIG.enabled;
    const persistenceMessage = persistenceEnabled
      ? 'with persistence enabled'
      : 'with persistence disabled';

    console.log(`🎉 Feature ${featureModule.id} successfully registered ${persistenceMessage}`);

    return {
      success: true,
      featureId: featureModule.id,
    };
  } catch (error) {
    console.error(`Failed to register feature ${featureModule.id}:`, error);
    return {
      success: false,
      featureId: featureModule.id,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Unregister a feature module
 */
export function unregisterFeatureModule(
  featureId: string,
  store: StoreWithReducerManager,
): RegistrationResult {
  try {
    const feature = featureModules.get(featureId);

    if (!feature) {
      return {
        success: false,
        featureId,
        error: 'Feature not found in registry',
      };
    }

    // Check if any other features depend on this one
    const dependentFeatures = findDependentFeatures(featureId);
    if (dependentFeatures.length > 0) {
      return {
        success: false,
        featureId,
        error: `Feature is a dependency for: ${dependentFeatures.join(', ')}`,
      };
    }

    // Run cleanup function if provided
    if (feature.cleanup) {
      feature.cleanup(store);
    }

    // Remove all reducers using the dynamic store manager
    Object.keys(feature.reducers).forEach(key => {
      removeReducer(key);

      // If this feature had persistence enabled and was using root persistence,
      // remove from whitelist
      if (feature.persistence?.enabled && !feature.persistence.key) {
        removeFromWhitelist(key);
      }
    });

    // Remove all middleware using Redux Toolkit dynamic middleware
    if (feature.middleware && feature.middleware.length > 0 && store.dynamicMiddleware) {
      feature.middleware.forEach(middleware => {
        store.dynamicMiddleware.removeMiddleware(middleware);
      });
    }

    // Remove from modules map
    featureModules.delete(featureId);

    // Dispatch unregistration action
    store.dispatch(unregisterFeature(featureId));

    return {
      success: true,
      featureId,
    };
  } catch (error) {
    console.error(`Failed to unregister feature ${featureId}:`, error);
    return {
      success: false,
      featureId,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Check if a feature is registered
 */
export function isFeatureRegistered(state: RootState, featureId: string): boolean {
  return Boolean(state.featureRegistry?.registeredFeatures[featureId]);
}

/**
 * Find all features that depend on a specific feature
 */
function findDependentFeatures(featureId: string): string[] {
  const dependents: string[] = [];

  featureModules.forEach((module, id) => {
    if (module.dependencies?.includes(featureId)) {
      dependents.push(id);
    }
  });

  return dependents;
}

/**
 * Get all registered features
 */
export function getRegisteredFeatures(state: RootState): string[] {
  return state.featureRegistry ? Object.keys(state.featureRegistry.registeredFeatures) : [];
}

/**
 * Get a registered feature module
 */
export function getFeatureModule(featureId: string): FeatureModule | undefined {
  return featureModules.get(featureId);
}

/**
 * Get persistence configuration for a feature
 */
export function getFeaturePersistenceConfig(featureId: string) {
  const feature = featureModules.get(featureId);

  if (!feature || !feature.persistence) {
    return null;
  }

  return {
    ...DEFAULT_PERSISTENCE_CONFIG,
    ...feature.persistence,
  };
}

/**
 * Check if all required features are registered
 */
export function areFeaturesDependenciesSatisfied(state: RootState, featureIds: string[]): boolean {
  for (const featureId of featureIds) {
    const feature = featureModules.get(featureId);

    if (!feature) {
      return false;
    }

    if (feature.dependencies) {
      for (const dependency of feature.dependencies) {
        if (!isFeatureRegistered(state, dependency)) {
          return false;
        }
      }
    }
  }

  return true;
}
